// Global search palette — ⌘K-opened, hits the IDB caches across
// notes / recipes / todos / shopping (no chat — chat has its own
// in-sidebar RAG search). Pure client-side; works offline.
//
// Results are grouped by domain with each group sorted by relevance.
// Clicking a row navigates to the appropriate section and, when
// supported, hands the section a focus hint so it lands the user on
// (or near) the matched item.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Icon, type IconName } from "../icons";
import { localStore, type CacheEntry } from "../lib/local-store";
import "../styles/global-search.css";

export type GlobalSearchSection = "notes" | "food" | "todo";
export type GlobalSearchFocus =
  | { kind: "note";     root?: string; path: string }
  | { kind: "recipe";   recipeId: number | string }
  | { kind: "shopping"; listId: number | string }
  | { kind: "pantry";   itemId: number | string }
  | { kind: "todo";     listId?: number | string };

export interface GlobalSearchPickHandler {
  (section: GlobalSearchSection, focus: GlobalSearchFocus): void;
}

interface PaletteProps {
  open: boolean;
  onClose: () => void;
  onPick: GlobalSearchPickHandler;
}

// Domain tag shown on each result row + the icon for its group header.
const DOMAIN_META: Record<Domain, { label: string; icon: IconName }> = {
  notes:    { label: "Notes",    icon: "notes" },
  recipes:  { label: "Recipes",  icon: "food" },
  pantry:   { label: "Pantry",   icon: "database" },
  todos:    { label: "Todos",    icon: "todo" },
  shopping: { label: "Shopping", icon: "food" },
};
const DOMAIN_ORDER: Domain[] = ["notes", "recipes", "pantry", "todos", "shopping"];

type Domain = "notes" | "recipes" | "pantry" | "todos" | "shopping";

interface Hit {
  domain: Domain;
  title: string;
  /** Short context line — note path, list name, etc. */
  context?: string;
  /** Higher = better match. */
  score: number;
  /** Focus payload handed to onPick. */
  focus: GlobalSearchFocus;
}

const MAX_PER_DOMAIN = 6;
const RECENT_PER_DOMAIN = 3;  // empty-query state

export function GlobalSearchPalette({ open, onClose, onPick }: PaletteProps) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset on open; focus input on next frame so the autofocus actually
  // lands (the backdrop's mount animation eats the synchronous focus).
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSelected(0);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  // Scan IDB caches whenever the query changes. Pure local — no
  // network — so debouncing is unnecessary. Cancellable for the case
  // where the user types fast enough to overlap.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const next = await collectHits(query);
      if (!cancelled) setHits(next);
    })();
    return () => { cancelled = true; };
  }, [query, open]);

  // Flatten the grouped result list into an ordered array for
  // keyboard navigation. The map preserves the visual order so the
  // selected index matches what the user sees scrolling through.
  const flat = useMemo(() => {
    const out: Hit[] = [];
    for (const d of DOMAIN_ORDER) {
      for (const h of hits) if (h.domain === d) out.push(h);
    }
    return out;
  }, [hits]);

  // Clamp selection when the result list shrinks underneath it.
  useEffect(() => {
    if (selected >= flat.length) setSelected(Math.max(0, flat.length - 1));
  }, [flat.length, selected]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(flat.length - 1, s + 1)); return; }
    if (e.key === "ArrowUp")   { e.preventDefault(); setSelected((s) => Math.max(0, s - 1)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = flat[selected];
      if (pick) {
        onPick(routeFor(pick.domain), pick.focus);
        onClose();
      }
    }
  };

  if (!open) return null;
  return (
    <div className="gs-backdrop" onClick={onClose}>
      <div
        className="gs-palette"
        role="dialog"
        aria-label="Global search"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gs-input">
          <Icon name="search" size={14} />
          <input
            ref={inputRef}
            placeholder="Search notes, recipes, todos, shopping…"
            value={query}
            onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={onKeyDown}
          />
          <kbd className="gs-hint">esc</kbd>
        </div>
        <div className="gs-list">
          {flat.length === 0 && query.trim() && (
            <div className="gs-empty">No matches.</div>
          )}
          {flat.length === 0 && !query.trim() && (
            <div className="gs-empty">
              Type to search across notes, recipes, todos, and shopping.
            </div>
          )}
          {DOMAIN_ORDER.map((d) => {
            const rows = hits.filter((h) => h.domain === d);
            if (rows.length === 0) return null;
            const meta = DOMAIN_META[d];
            return (
              <div key={d}>
                <div className="gs-group">
                  <Icon name={meta.icon} size={11} />
                  <span>{meta.label}</span>
                </div>
                {rows.map((h) => {
                  const flatIdx = flat.indexOf(h);
                  return (
                    <button
                      key={`${d}-${h.title}-${flatIdx}`}
                      type="button"
                      className="gs-row"
                      data-active={flatIdx === selected}
                      onMouseEnter={() => setSelected(flatIdx)}
                      onClick={() => { onPick(routeFor(h.domain), h.focus); onClose(); }}
                    >
                      <div className="gs-row-title">{h.title}</div>
                      {h.context && <div className="gs-row-context">{h.context}</div>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function routeFor(d: Domain): GlobalSearchSection {
  if (d === "notes") return "notes";
  if (d === "todos") return "todo";
  return "food";  // recipes + pantry + shopping all live in Food
}

// ─────────────────────────────────────────────────────────────────
// Data collection + scoring. Everything reads from IDB; the same
// hooks that populate the caches on online RPC calls also keep them
// warm, so the palette is functional offline.
// ─────────────────────────────────────────────────────────────────

type AnyRecord = Record<string, any>;

async function collectHits(rawQuery: string): Promise<Hit[]> {
  const q = rawQuery.trim().toLowerCase();
  const [notes, recipes, pantry, todos, shopping] = await Promise.all([
    collectNotes(q),
    collectRecipes(q),
    collectPantry(q),
    collectTodos(q),
    collectShopping(q),
  ]);
  return [...notes, ...recipes, ...pantry, ...todos, ...shopping];
}

// Empty query: pick the N most-recently-touched items. Falls back to
// "first N" when no recency signal (mtime / id ordering) is present.
function emptyState<T extends Hit>(items: T[], n = RECENT_PER_DOMAIN): T[] {
  return items.slice(0, n);
}

async function collectNotes(q: string): Promise<Hit[]> {
  const dirEntries = await localStore.getAll<AnyRecord>("notes_dirs").catch(() => [] as CacheEntry<AnyRecord>[]);
  // notes_dirs key = `${root}:${path}` → value = { ok, entries: [{name, type, mtimeMs}] }.
  // Flatten to one row per file (ignore dirs).
  const rows: Hit[] = [];
  for (const e of dirEntries) {
    const value = e.value || {};
    const entries: AnyRecord[] = value.entries || value.files || [];
    if (!Array.isArray(entries)) continue;
    const [root, dirPath = ""] = String(e.key).split(":");
    for (const entry of entries) {
      if (!entry || entry.type === "dir") continue;
      const name: string = entry.name || "";
      if (!name || name.startsWith(".")) continue;
      const fullPath = dirPath ? `${dirPath}/${name}` : name;
      const dot = name.lastIndexOf(".");
      const title = dot > 0 ? name.slice(0, dot) : name;
      const mtimeMs = typeof entry.mtimeMs === "number" ? entry.mtimeMs : 0;
      const score = q ? matchScore(q, title) + matchScore(q, fullPath) * 0.4 : mtimeMs / 1e9;
      if (q && score <= 0) continue;
      rows.push({
        domain: "notes",
        title,
        context: `${root} · ${dirPath || "/"}`,
        score,
        focus: { kind: "note", root, path: fullPath },
      });
    }
  }
  rows.sort((a, b) => b.score - a.score);
  // Dedupe by (root, path) — a note can show up in several cached
  // listings if the user browsed multiple dirs that all contained it.
  const seen = new Set<string>();
  const dedup: Hit[] = [];
  for (const r of rows) {
    const f = r.focus as Extract<GlobalSearchFocus, { kind: "note" }>;
    const k = `${f.root}:${f.path}`;
    if (seen.has(k)) continue;
    seen.add(k);
    dedup.push(r);
    if (dedup.length >= MAX_PER_DOMAIN) break;
  }
  return q ? dedup : emptyState(dedup);
}

async function collectRecipes(q: string): Promise<Hit[]> {
  const entry = await localStore.get<AnyRecord>("recipes_list", "_").catch(() => undefined);
  const recipes: AnyRecord[] = entry?.value?.recipes || [];
  const rows: Hit[] = [];
  for (const r of recipes) {
    const title: string = r.name || `Recipe ${r.id}`;
    const tagList: string[] = Array.isArray(r.tags) ? r.tags : [];
    const tagBlob = tagList.join(" ");
    const score = q
      ? matchScore(q, title) + matchScore(q, tagBlob) * 0.5
      : 0;
    if (q && score <= 0) continue;
    rows.push({
      domain: "recipes",
      title,
      context: tagList.length > 0 ? tagList.map((t) => `#${t}`).join(" ") : undefined,
      score,
      focus: { kind: "recipe", recipeId: r.id },
    });
  }
  rows.sort((a, b) => b.score - a.score);
  return (q ? rows : emptyState(rows)).slice(0, MAX_PER_DOMAIN);
}

async function collectPantry(q: string): Promise<Hit[]> {
  const entry = await localStore.get<AnyRecord>("pantry_list", "_").catch(() => undefined);
  const items: AnyRecord[] = entry?.value?.items || [];
  const rows: Hit[] = [];
  const todayIso = new Date().toISOString().slice(0, 10);
  for (const it of items) {
    const name: string = it.name || "";
    if (!name) continue;
    const score = q
      ? matchScore(q, name) + matchScore(q, it.category || "") * 0.4
      : (it.lowStock ? 5 : (it.expires && it.expires <= todayIso ? 10 : 1));
    if (q && score <= 0) continue;
    // Context line: qty plus state badges (low / expiring / expired)
    const ctxBits: string[] = [it.qty || ""];
    if (it.lowStock) ctxBits.push("· low");
    if (it.expires) {
      if (it.expires < todayIso) ctxBits.push("· expired");
      else {
        const days = Math.floor((Date.parse(it.expires) - Date.now()) / 86400000);
        if (days <= 3) ctxBits.push(`· ${days}d`);
      }
    }
    rows.push({
      domain: "pantry",
      title: name,
      context: ctxBits.filter(Boolean).join(" "),
      score,
      focus: { kind: "pantry", itemId: it.id },
    });
  }
  rows.sort((a, b) => b.score - a.score);
  return (q ? rows : emptyState(rows)).slice(0, MAX_PER_DOMAIN);
}

async function collectTodos(q: string): Promise<Hit[]> {
  const listsEntry = await localStore.get<AnyRecord>("todos_lists", "_").catch(() => undefined);
  const lists: AnyRecord[] = listsEntry?.value?.lists || [];
  const listById = new Map<string, AnyRecord>();
  for (const l of lists) listById.set(String(l.id), l);

  const itemEntries = await localStore.getAll<AnyRecord>("todos_items").catch(() => [] as CacheEntry<AnyRecord>[]);
  const rows: Hit[] = [];
  for (const e of itemEntries) {
    const items: AnyRecord[] = e.value?.items || [];
    const listMeta = listById.get(String(e.key));
    const listName = listMeta?.name || "Inbox";
    for (const it of items) {
      const title: string = it.title || it.text || "";
      if (!title) continue;
      const body: string = it.body || "";
      // Match against title (primary) + body (half-weight) — a body
      // hit on "shopping list" without a title hit still surfaces if
      // the user types something specific they know is in the body.
      const score = q
        ? matchScore(q, title) + matchScore(q, body) * 0.5
        : (it.done ? 0 : 1);
      if (q && score <= 0) continue;
      rows.push({
        domain: "todos",
        title,
        context: `${listName}${it.done ? " · done" : ""}`,
        score,
        focus: { kind: "todo", listId: e.key },
      });
    }
  }
  rows.sort((a, b) => b.score - a.score);
  return (q ? rows : emptyState(rows)).slice(0, MAX_PER_DOMAIN);
}

async function collectShopping(q: string): Promise<Hit[]> {
  const listsEntry = await localStore.get<AnyRecord>("shopping_lists", "_").catch(() => undefined);
  const lists: AnyRecord[] = listsEntry?.value?.lists || [];
  const listById = new Map<string, AnyRecord>();
  for (const l of lists) listById.set(String(l.id), l);

  const itemEntries = await localStore.getAll<AnyRecord>("shopping_items").catch(() => [] as CacheEntry<AnyRecord>[]);
  const rows: Hit[] = [];
  for (const e of itemEntries) {
    const items: AnyRecord[] = e.value?.items || [];
    const listMeta = listById.get(String(e.key));
    const listName = listMeta?.name || "List";
    for (const it of items) {
      const text: string = it.text || "";
      if (!text) continue;
      const qty: string = it.quantity || "";
      const score = q
        ? matchScore(q, text) + matchScore(q, qty) * 0.3
        : (it.purchased ? 0 : 1);
      if (q && score <= 0) continue;
      const ctxBits = [listName];
      if (qty) ctxBits.push(qty);
      if (it.purchased) ctxBits.push("✓ got it");
      rows.push({
        domain: "shopping",
        title: text,
        context: ctxBits.join(" · "),
        score,
        focus: { kind: "shopping", listId: e.key },
      });
    }
  }
  rows.sort((a, b) => b.score - a.score);
  return (q ? rows : emptyState(rows)).slice(0, MAX_PER_DOMAIN);
}

// Relevance heuristic. Boosts:
//  - prefix match >> word-boundary match >> substring match
//  - shorter title containing the match wins over longer
// Returns 0 when there's no match at all so the caller can skip the row.
function matchScore(query: string, target: string): number {
  if (!query || !target) return 0;
  const t = target.toLowerCase();
  const i = t.indexOf(query);
  if (i < 0) return 0;
  // Prefix gets 100, word-boundary 70, anywhere 40.
  let base = 40;
  if (i === 0) base = 100;
  else if (/\W/.test(t.charAt(i - 1))) base = 70;
  // Length penalty: same match in a shorter string is more relevant.
  const lengthBoost = Math.max(0, 30 - target.length / 4);
  return base + lengthBoost;
}
